import * as React from "react";
import { GatsbyImage } from "gatsby-plugin-image";
import LazyLoad from "react-lazyload";
import GoogleRating from "./google-rating";
import { FormattedMessage } from "react-intl";
import Modal from "./modal";
import Slideshow from "./slideshow";
import Pictures from "./pictures";
import BookingModal from "./booking-modal";
import QuestionModal from "./question-modal";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import Tooltip from "./tooltip";
import Badges from "./badges";

const renderOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      const [{ value }] = node.content;

      if (value !== "" || node.content.length > 1) {
        return <p>{children}</p>;
      }

      return null;
    },

    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri}>{children}</Link>
    ),
  },

  renderText: (text) => {
    return text
      .split("\n")
      .flatMap((text, index) => [index > 0 && <br key={index} />, text]);
  },
};

const Hero = ({
  title,
  logo,
  intro,
  media,
  mediaMobile,
  badge,
  badges,
  pageTitle,
  reviews,
  distance,
  duration,
  difficulty,
  isProductPage = false,
  variant,
  imagesWithText,
  referenceProduct,
  stayReference,
  modalLevels,
  urlFillout,
  headerButtonLabel,
}) => {
  const mediaClass = media ? " hero-with-media" : "";
  const headerClass = media ? "hero-title" : "heading";
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const imageToDisplay = isMobile && mediaMobile ? mediaMobile : media;
  const bookingModalTriggerRef = React.useRef(null);
  const slideshowModalTriggerRef = React.useRef(null);
  const questionModalTriggerRef = React.useRef(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = React.useState(false);
  const [isSlideshowModalOpen, setIsSlideshowModalOpen] = React.useState(false);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = React.useState(false);
  const openBookingModal = () => setIsBookingModalOpen(true);
  const closeBookingModal = () => setIsBookingModalOpen(false);
  const openQuestionModal = () => setIsQuestionModalOpen(true);
  const closeQuestionModal = () => setIsQuestionModalOpen(false);
  const openSlideshowModal = () => setIsSlideshowModalOpen(true);
  const closeSlideshowModal = () => setIsSlideshowModalOpen(false);

  return (
    <header className={`hero${mediaClass}`} data-variant={variant}>
      <div className="container">
        <div>
          <Badges badges={badges} />
          {pageTitle === false ? (
            <span className={headerClass}>
              {logo ? (
                <div className="hero-logo">
                  <GatsbyImage
                    objectFit="cover"
                    image={logo.gatsbyImageData}
                    alt={logo.title}
                  />
                </div>
              ) : (
                title
              )}
            </span>
          ) : (
            <h1 className={headerClass}>
              {logo ? (
                <div className="hero-logo">
                  <GatsbyImage
                    objectFit="cover"
                    image={logo.gatsbyImageData}
                    alt={logo.title}
                  />
                </div>
              ) : (
                title
              )}
            </h1>
          )}
          {intro && (
            <p
              className="introduction"
              dangerouslySetInnerHTML={{ __html: intro }}
            />
          )}
          {reviews && (
            <GoogleRating variant={badge ? "hero-with-badge" : "hero"} />
          )}
          {(distance || duration || difficulty) && (
            <dl className="hero-product-info figures">
              {duration && (
                <div className="figure">
                  <dt>
                    <FormattedMessage id="metaDuration" />
                  </dt>
                  <dd>{duration}</dd>
                </div>
              )}
              {distance && (
                <div className="figure">
                  <dt>
                    <FormattedMessage id="metaDistance" />
                  </dt>
                  <dd>{distance}</dd>
                </div>
              )}
              {difficulty && (
                <div className="figure">
                  <dt>
                    <FormattedMessage id="metaDifficulty" />
                    <button
                      className="figure-difficulty-tooltip"
                      onClick={openQuestionModal}
                      ref={questionModalTriggerRef}
                    >
                      <Tooltip />
                    </button>
                  </dt>
                  <dd>{difficulty} / 5</dd>
                  <button
                    className="link figure-difficulty-link"
                    onClick={openQuestionModal}
                  >
                    <FormattedMessage id="campDestinationsLink" />
                  </button>
                </div>
              )}
            </dl>
          )}
          {isProductPage && (
            <a
              className="button-ghost"
              onClick={openBookingModal}
              ref={bookingModalTriggerRef}
            >
              {headerButtonLabel ? (
                headerButtonLabel
              ) : (
                <FormattedMessage id="viewDeparturesHeader" />
              )}
            </a>
          )}
          {imagesWithText && (
            <button
              className="link hero-slideshow-link"
              onClick={openSlideshowModal}
              ref={slideshowModalTriggerRef}
            >
              <Pictures />
              <FormattedMessage id="seePictures" />
            </button>
          )}
        </div>
      </div>
      {imageToDisplay && (
        <figure>
          {imageToDisplay.gatsbyImageData && (
            <GatsbyImage
              image={imageToDisplay.gatsbyImageData}
              alt={imageToDisplay.title}
              loading="eager"
              fetchpriority="high"
              aria-hidden={media.video ? true : undefined}
            />
          )}
          {media.video && (
            <LazyLoad>
              <video
                width={1920}
                height={1080}
                autoPlay
                muted
                loop
                playsInline
                aria-hidden
              >
                <source src={media.video.publicURL} type="video/mp4" />
              </video>
            </LazyLoad>
          )}
        </figure>
      )}
      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={closeBookingModal}
        referenceProduct={referenceProduct}
        stayReference={stayReference}
        destination={title}
        triggerRef={bookingModalTriggerRef}
        urlFillout={urlFillout}
      />
      <QuestionModal
        isOpen={isQuestionModalOpen}
        onClose={closeQuestionModal}
        modalLevels={modalLevels}
        renderOptions={renderOptions}
        triggerRef={questionModalTriggerRef}
      />
      <Modal
        isOpen={isSlideshowModalOpen}
        onClose={closeSlideshowModal}
        triggerRef={slideshowModalTriggerRef}
        variant="photo"
      >
        <Slideshow slides={imagesWithText} fullWidth product />
      </Modal>
    </header>
  );
};

export default Hero;
